/**
 * RTK Query endpoints for the odl-app SiteGroup resource.
 *
 * Covers retrieve, create, patch, and the bulk actions: transfer (moves
 * selected groups, with their sites, to another customer), deactivate
 * (with its dry-run preflight), and reactivate. The destroy action on
 * the BE viewset raises PermissionDenied by design. Grid list reads are
 * served by EntityGrid's dataSource and are not duplicated here.
 */

import { entitiesApi } from './entitiesApi';

export const siteGroupsApi = entitiesApi.injectEndpoints({
  endpoints: build => ({
    retrieveSiteGroup: build.query({
      query: ({ id }) => ({ url: `site-groups/${id}/` }),
      transformResponse: b => b.data,
      providesTags: (result, error, { id }) => [{ type: 'SiteGroup', id }],
    }),

    createSiteGroup: build.mutation({
      query: ({ body }) => ({ url: 'site-groups/', method: 'post', data: body }),
      transformResponse: b => b.data,
      invalidatesTags: [{ type: 'SiteGroup', id: 'LIST' }],
    }),

    patchSiteGroup: build.mutation({
      query: ({ id, body }) => ({ url: `site-groups/${id}/`, method: 'patch', data: body }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'SiteGroup', id: 'LIST' },
        { type: 'SiteGroup', id },
      ],
    }),

    /**
     * Dry-run a bulk site-group transfer. Writes nothing; returns
     * { transferable, skipped, not_found, colliding_names, moved_site_count,
     *   severed_user_count, severed_share_count, severed_auto_share_count,
     *   unrelated_third_parties }.
     *
     * `unrelated_third_parties` are the customers on the far side of a share
     * that the destination holds no third-party agreement with — the transfer
     * refuses unless the caller drops those shares or grants the agreements.
     * `unreachable_users` are the people assigned to the moving groups who
     * would not be able to see them afterwards; there is no auto-grant for
     * them, so keeping their assignment means fixing their access by hand.
     *
     * @param {{ siteGroupIds?: Array<number>, selectAll?: boolean, filters?: object,
     *           targetCustomerId: number }} arg
     *        `filters` is the grid's JSON:API param dict (filter[<field>] keys),
     *        used only when `selectAll` is set.
     */
    preflightTransferSiteGroups: build.mutation({
      query: ({ siteGroupIds, selectAll, filters, targetCustomerId }) => ({
        url: 'site-groups/bulk/preflight/',
        method: 'post',
        data: selectAll
          ? { action: 'transfer', select_all: true, filters, target_customer_id: targetCustomerId }
          : { action: 'transfer', site_group_ids: siteGroupIds, target_customer_id: targetCustomerId },
      }),
      transformResponse: b => b.data ?? b,
    }),

    /**
     * Bulk-transfer site groups to a target customer — explicit ids or
     * select-all over the current grid filters. Returns
     * { transferred, skipped, not_found, moved_site_count, severed_user_count,
     *   severed_share_count, severed_auto_share_count,
     *   created_relationship_count }.
     *
     * `shareResolution` decides what happens to shares that would outlive the
     * move: 'remove' drops them, 'grant' gives the destination the missing
     * third-party agreements so they stay valid. Omit it and the backend
     * refuses the transfer when either would be needed.
     *
     * `userHandling` decides the moved groups' assignments: 'keep_reachable'
     * (the default) keeps only people who can see the destination,
     * 'remove_all' clears them, 'keep_all' keeps every one — including rows
     * that show their holder nothing until their access is widened by hand.
     *
     * @param {{ siteGroupIds?: Array<number>, selectAll?: boolean, filters?: object,
     *           targetCustomerId: number, shareResolution?: 'remove'|'grant',
     *           userHandling?: 'remove_all'|'keep_reachable'|'keep_all' }} arg
     *        `filters` is the grid's JSON:API param dict (filter[<field>] keys),
     *        used only when `selectAll` is set.
     */
    transferSiteGroups: build.mutation({
      query: ({ siteGroupIds, selectAll, filters, targetCustomerId, shareResolution, userHandling }) => ({
        url: 'site-groups/bulk/',
        method: 'post',
        data: {
          action: 'transfer',
          target_customer_id: targetCustomerId,
          ...(selectAll ? { select_all: true, filters } : { site_group_ids: siteGroupIds }),
          ...(shareResolution === 'remove' ? { remove_third_party_associations: true } : {}),
          ...(shareResolution === 'grant' ? { create_third_party_relationships: true } : {}),
          ...(userHandling ? { user_handling: userHandling } : {}),
        },
      }),
      transformResponse: b => b.data ?? b,
      invalidatesTags: [{ type: 'SiteGroup', id: 'LIST' }],
    }),

    /**
     * Dry-run a bulk site-group deactivation. Writes nothing; returns
     * { site_groups, virtual_count, regular_count, with_active_sites,
     *   total_active_sites, total_units, has_cellular, already_deactivated,
     *   not_found }.
     *
     * @param {{ siteGroupIds?: Array<number>, selectAll?: boolean, filters?: object }} arg
     *        `filters` is the grid's JSON:API param dict (filter[<field>] keys),
     *        used only when `selectAll` is set.
     */
    preflightDeactivateSiteGroups: build.mutation({
      query: ({ siteGroupIds, selectAll, filters }) => ({
        url: 'site-groups/bulk/preflight/',
        method: 'post',
        data: selectAll
          ? { action: 'deactivate', select_all: true, filters }
          : { action: 'deactivate', site_group_ids: siteGroupIds },
      }),
      transformResponse: b => b.data ?? b,
    }),

    /**
     * Bulk-deactivate site groups — explicit ids or select-all over the
     * current grid filters. Returns
     * { deactivated, already_deactivated, not_found }.
     *
     * `radioAction` decides what happens to units on the groups' active
     * sites: 'customer_inventory' (default), 'purge' (SIMs deactivated at
     * the carrier — irreversible), 'relocate_sites' (sites move to the
     * destination group and stay active), or 'relocate_radios' (sites
     * deactivate, radios rest in the destination group's inventory). The
     * relocate actions require `destinationSiteGroupId`.
     *
     * @param {{ siteGroupIds?: Array<number>, selectAll?: boolean, filters?: object,
     *           radioAction?: 'customer_inventory'|'purge'|'relocate_sites'|'relocate_radios',
     *           destinationSiteGroupId?: number }} arg
     *        `filters` is the grid's JSON:API param dict (filter[<field>] keys),
     *        used only when `selectAll` is set.
     */
    deactivateSiteGroups: build.mutation({
      query: ({ siteGroupIds, selectAll, filters, radioAction, destinationSiteGroupId }) => ({
        url: 'site-groups/bulk/',
        method: 'post',
        data: {
          action: 'deactivate',
          ...(selectAll ? { select_all: true, filters } : { site_group_ids: siteGroupIds }),
          ...(radioAction ? { radio_action: radioAction } : {}),
          ...(destinationSiteGroupId ? { destination_site_group_id: destinationSiteGroupId } : {}),
        },
      }),
      transformResponse: b => b.data ?? b,
      invalidatesTags: [{ type: 'SiteGroup', id: 'LIST' }],
    }),

    /**
     * Bulk-reactivate deactivated site groups. A flag flip only — nothing
     * torn down by deactivation (memberships, assignments, radios, SIMs)
     * comes back. Returns { activated, already_active, not_found }.
     *
     * @param {{ siteGroupIds: Array<number> }} arg
     */
    reactivateSiteGroups: build.mutation({
      query: ({ siteGroupIds }) => ({
        url: 'site-groups/bulk-activate/',
        method: 'post',
        data: { site_group_ids: siteGroupIds },
      }),
      transformResponse: b => b.data ?? b,
      invalidatesTags: [{ type: 'SiteGroup', id: 'LIST' }],
    }),
  }),
});

export const {
  useRetrieveSiteGroupQuery,
  useCreateSiteGroupMutation,
  usePatchSiteGroupMutation,
  useTransferSiteGroupsMutation,
  usePreflightTransferSiteGroupsMutation,
  usePreflightDeactivateSiteGroupsMutation,
  useDeactivateSiteGroupsMutation,
  useReactivateSiteGroupsMutation,
} = siteGroupsApi;
